import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Plus, X, Trash2, Pencil } from "lucide-react";

const FIELDS = [
  { key: "name", label: "Company Name", required: true },
  { key: "country", label: "Country" },
  { key: "industry", label: "Industry" },
  { key: "revenue", label: "Revenue" },
  { key: "website", label: "Website" },
];

export default function CrmCompanies() {
  const { t } = useI18n();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = async () => {
    const { data } = await api.get("/crm-companies");
    setList(data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({}); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm(c); setOpen(true); };
  const submit = async (e) => {
    e.preventDefault();
    if (editing) await api.patch(`/crm-companies/${editing.id}`, form);
    else await api.post("/crm-companies", form);
    setOpen(false); load();
  };
  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/crm-companies/${id}`); load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{t("nav.companies")}</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight mt-1">CRM Companies</h1>
        </div>
        <button onClick={openCreate} data-testid="crm-company-create-button" className="inline-flex items-center gap-2 px-4 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold">
          <Plus className="w-4 h-4" /> {t("common.add")}
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="crm-companies-table">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {["Name", "Country", "Industry", "Revenue", t("common.actions")].map((h) => (
                <th key={h} className="py-3 px-4 text-left text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan={5} className="py-8 text-center text-slate-400">{t("common.no_data")}</td></tr>
            ) : list.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="py-3 px-4 font-medium text-slate-900">{c.name}</td>
                <td className="py-3 px-4">{c.country || "—"}</td>
                <td className="py-3 px-4">{c.industry || "—"}</td>
                <td className="py-3 px-4">{c.revenue || "—"}</td>
                <td className="py-3 px-4 text-right">
                  <button onClick={() => openEdit(c)} className="p-1.5 text-slate-500 hover:text-[#0052CC]"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(c.id)} className="p-1.5 text-slate-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-lg max-w-md w-full border border-slate-200" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900">{editing ? t("common.edit") : t("common.add")}</h3>
              <button onClick={() => setOpen(false)}><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="px-6 py-4 space-y-3" data-testid="crm-company-form">
              {FIELDS.map((f) => (
                <div key={f.key}>
                  <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{f.label}</label>
                  <input data-testid={`crm-company-${f.key}`} required={f.required} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
                </div>
              ))}
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">Notes</label>
                <textarea data-testid="crm-company-notes" value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm min-h-[60px]" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border border-slate-200 rounded-md text-sm font-medium">{t("common.cancel")}</button>
                <button type="submit" data-testid="crm-company-submit" className="px-4 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold">{t("common.save")}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
